import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { Calendar as BigCalendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Loader2, Sparkles, Eye, EyeOff, AlertCircle, Plus,
  Users, User, Repeat, Layers, Banknote, Hourglass,
} from 'lucide-react';import 'react-big-calendar/lib/css/react-big-calendar.css';
import './expert-calendar.css';
import EventCreationWizard from './EventCreationWizard';
import EventDetailsModal from './EventDetailsModal';
import SessionDetailsDrawer from './SessionDetailsDrawer';
import { toast } from 'sonner';
import { parseUtc } from '../lib/calendarDate';
import { eventStyleFor, EVENT_LEGEND, makeCalendarToolbar } from './calendarTheme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Map event type → visual identity (glassmorphism teal/violet + Steve's color code)
// Complet (sold-out) = GRIS FONCÉ (not red — sold-out is success not error).
const TYPE_META = {
  private_1on1:    { label: '1-to-1 privé',         icon: User,   tint: 'violet' },
  public_slot:     { label: 'Slot public',          icon: Repeat, tint: 'teal'   },
  public_event:    { label: 'Event public',         icon: Users,  tint: 'amber'  },
  public_series:   { label: 'Série récurrente',     icon: Layers, tint: 'cyan'   },
};

const locales = { fr };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }), getDay, locales });

// Brief 27 oct LOT E.1 (RCA) — la correction iter_89 (540→680) visait le calendrier
// du profil public ; « Mes sessions » (mode compact) restait à 480px ≈ 8h30 visibles.
// 680px ≈ 12h visibles (toolbar + en-tête ≈ 100px, ~48px/heure), aligné sur le profil.
const COMPACT_HEIGHT = 680;

// react-big-calendar i18n
const FR_MESSAGES = {
  date: 'Date', time: 'Heure', event: 'Événement',
  allDay: 'Toute la journée', week: 'Semaine', work_week: 'Semaine ouvrée',
  day: 'Jour', month: 'Mois', previous: '←', next: '→', yesterday: 'Hier',
  tomorrow: 'Demain', today: 'Aujourd\'hui', agenda: 'Agenda',
  noEventsInRange: 'Aucun événement sur cette période.',
  showMore: (n) => `+${n} de plus`,
};

const TYPE_FILTERS = [
  { id: 'private_1on1', label: '1-to-1', testid: 'cal-filter-private' },
  { id: 'public_slot', label: 'Slots',  testid: 'cal-filter-slot' },
  { id: 'public_event', label: 'Events', testid: 'cal-filter-event' },
  { id: 'public_series', label: 'Séries', testid: 'cal-filter-series' },
];

// Brief Jun 11 — toolbar custom : sélecteur de vue + légende couleurs dessous.
const ExpertCalendarToolbar = makeCalendarToolbar(EVENT_LEGEND);

// Brief Jul 6 2026 P1 — vue Outlook-like : contenu custom des events de la
// grille avec nb d'inscrits + statuts de paiement (payé / en attente).
const CalendarEventContent = ({ event }) => {
  const r = event.resource || {};
  const stats = r.booking_stats;
  const showStats = !r._kind && stats && stats.total > 0;
  return (
    <div className="os-cal-event" data-testid={r.event_id ? `cal-event-content-${r.event_id}` : undefined}>
      <span className="os-cal-event-title">{event.title}</span>
      {showStats && (
        <span className="os-cal-event-badges">
          <span
            className="os-cal-badge"
            title={`${stats.total} inscrit${stats.total > 1 ? 's' : ''}`}
            data-testid={`cal-event-attendees-${r.event_id}`}
          >
            <Users className="h-2.5 w-2.5" />{stats.total}
          </span>
          {stats.paid_count > 0 && (
            <span
              className="os-cal-badge os-cal-badge-paid"
              title={`${stats.paid_count} payé(s) · ${stats.revenue_paid} CHF`}
              data-testid={`cal-event-paid-${r.event_id}`}
            >
              <Banknote className="h-2.5 w-2.5" />{stats.paid_count}
            </span>
          )}
          {stats.pending_payment_count > 0 && (
            <span
              className="os-cal-badge os-cal-badge-pending"
              title={`${stats.pending_payment_count} paiement(s) en attente`}
              data-testid={`cal-event-pending-${r.event_id}`}
            >
              <Hourglass className="h-2.5 w-2.5" />{stats.pending_payment_count}
            </span>
          )}
        </span>
      )}
    </div>
  );
};

// Brief Jun 11 — parse du début d'une session réservée (rôle client).
// Les bookings legacy stockent start_time "HH:MM" + date "YYYY-MM-DD" (heure
// locale CH) ; certains rows portent un ISO complet ou un session_date.
const parseBookingStart = (s) => {
  const st = s.start_time;
  if (typeof st === 'string' && st.includes('T')) return parseUtc(st);
  if (s.date && typeof st === 'string' && /^\d{2}:\d{2}/.test(st)) {
    const d = new Date(`${s.date}T${st.slice(0, 5)}:00`);
    return isNaN(d) ? null : d;
  }
  if (s.session_date) {
    const d = new Date(s.session_date);
    return isNaN(d) ? null : d;
  }
  return null;
};

// Expand recurring weekly events into concrete date instances inside the
// current view window. Phase α: pre-computed occurrences are not used yet, we
// just project each weekly event 8 weeks forward starting from start_time.
// Phase β will store concrete occurrences (or use RRULE) — for now this gives
// Steve a faithful read-only preview.
const expandOccurrences = (event, windowStart, windowEnd) => {
  const occ = [];
  // CRITICAL: interpret backend datetimes as UTC (cf. /lib/calendarDate.js fix β)
  const baseStart = parseUtc(event.start_time);
  const baseEnd = parseUtc(event.end_time);
  const recPattern = event.recurrence?.pattern;
  const daysOfWeek = event.recurrence?.days_of_week || [];

  if (recPattern !== 'weekly' || daysOfWeek.length === 0) {
    occ.push({ start: baseStart, end: baseEnd, original: event });
    return occ;
  }

  // Walk from windowStart to windowEnd, emitting one occurrence on each matching weekday
  const durMs = baseEnd - baseStart;
  let cursor = new Date(windowStart);
  cursor.setHours(baseStart.getHours(), baseStart.getMinutes(), 0, 0);
  while (cursor <= windowEnd) {
    // date-fns getDay: Sun=0..Sat=6 ; our model: Mon=0..Sun=6 → translate
    const isoWeekday = (cursor.getDay() + 6) % 7;
    if (daysOfWeek.includes(isoWeekday) && cursor >= baseStart) {
      occ.push({
        start: new Date(cursor),
        end: new Date(cursor.getTime() + durMs),
        original: event,
      });
    }
    cursor = addDays(cursor, 1);
  }
  return occ;
};

const ExpertCalendar = ({ ownerUserId, compact = false, hideNewButton = false }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [view, setView] = useState(Views.WEEK);
  const [date, setDate] = useState(new Date());
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [detailsEvent, setDetailsEvent] = useState(null);
  // Brief Jul 27 2026 §5 — drawer de détails pour les sessions réservées /
  // invitations (rôle non-expert) cliquées dans le calendrier.
  const [detailsRow, setDetailsRow] = useState(null);
  const [fuseSlots, setFuseSlots] = useState(true);  // Steve confirmed for β
  // Brief Jun 11 — sessions réservées par moi (rôle client), visibles uniquement
  // dans MON calendrier privé (jamais sur le profil public).
  const [bookedRows, setBookedRows] = useState([]);
  const [enabledTypes, setEnabledTypes] = useState({
    private_1on1: true,
    public_slot: true,
    public_event: true,
    public_series: true,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [evRes, statsRes, mineRes] = await Promise.all([
        axios.get(`${API}/calendar/events`, { withCredentials: true }),
        axios.get(`${API}/calendar/stats`, { withCredentials: true }),
        // Brief Jun 11 — sessions que J'AI réservées (rôle client) pour la grille
        axios.get(`${API}/me/all-sessions`, { withCredentials: true }).catch(() => ({ data: { sessions: [] } })),
      ]);
      setEvents(evRes.data.events || []);
      setStats(statsRes.data);
      setBookedRows((mineRes.data.sessions || []).filter(
        (s) => (s.kind === 'booking'
          && s.viewer_role === 'client'
          && ['pending', 'pending_payment', 'confirmed'].includes(s.status))
          // Brief Jul 27 2026 §5 — invitations en attente visibles dans le
          // calendrier (logique Outlook), cliquables → drawer Accepter/Décliner.
          || (s.kind === 'invitation' && s.status === 'pending')
      ));
    } catch (e) {
      setError(e.response?.data?.detail || 'Impossible de charger le calendrier.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, ownerUserId]);

  // Compute the visible window for occurrence expansion (1 month either side of `date`).
  const windowStart = useMemo(() => addDays(date, -31), [date]);
  const windowEnd = useMemo(() => addDays(date, 62), [date]);

  // Brief Aug 10 2026 — tick 60 s : le préfixe "🔴 En cours" du calendrier se
  // met à jour sans rechargement de page.
  const [liveTick, setLiveTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setLiveTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, []);

  const bigCalEvents = useMemo(() => {
    const out = [];
    // Brief Aug 10 2026 — préfixe "🔴 " sur les sessions en cours maintenant.
    const liveDot = (start, end) => {
      const n = new Date();
      return start && end && n >= start && n <= end ? '🔴 ' : '';
    };

    // Brief Jun 11 — sessions réservées par moi (teal #1D9E75), toujours visibles
    // (non soumises aux filtres de type — c'est une dimension "apprenant").
    // Brief audit beta Feb 2026 (P0 Bug 3) — préfixe "En attente · " pour les
    // bookings status=pending afin de rendre l'état explicite dans le calendrier.
    const bookedEvents = [];
    for (const s of bookedRows) {
      const start = parseBookingStart(s);
      if (!start) continue;
      const end = new Date(start.getTime() + (Number(s.duration) || 60) * 60000);
      const isPending = s.status === 'pending' || s.status === 'pending_payment';
      const prefix = s.kind === 'invitation' ? 'Invitation · ' : (isPending ? 'En attente · ' : 'Réservé · ');
      bookedEvents.push({
        title: `${liveDot(start, end)}${prefix}${s.title || s.counterpart?.display_name || 'Session'}`,
        start,
        end,
        resource: { ...s, _kind: 'booked_by_me' },
      });
    }

    // First expand all enabled events into occurrences within the window
    const expanded = [];
    for (const e of events) {
      if (!enabledTypes[e.type]) continue;
      const occ = expandOccurrences(e, windowStart, windowEnd);
      for (const o of occ) {
        expanded.push({
          start: o.start,
          end: o.end,
          original: e,
        });
      }
    }

    // Optionally fuse public_slot occurrences that fall on the EXACT same start time
    // (same minute) — Steve validated for β: 1 horaire = N session_types au choix.
    if (!fuseSlots) {
      return [
        ...expanded.map((o) => ({
          title: `${liveDot(o.start, o.end)}${o.original.title}`,
          start: o.start,
          end: o.end,
          resource: o.original,
        })),
        ...bookedEvents,
      ];
    }

    const groups = new Map();  // key = `slot_${startISO}` → {start, end, options: [event,...]}
    for (const o of expanded) {
      if (o.original.type !== 'public_slot') {
        out.push({
          title: `${liveDot(o.start, o.end)}${o.original.title}`,
          start: o.start,
          end: o.end,
          resource: o.original,
        });
        continue;
      }
      const key = `slot_${o.start.toISOString()}`;
      if (!groups.has(key)) {
        groups.set(key, { start: o.start, end: o.end, options: [] });
      }
      groups.get(key).options.push(o.original);
    }
    for (const g of groups.values()) {
      if (g.options.length === 1) {
        out.push({
          title: `${liveDot(g.start, g.end)}${g.options[0].title}`,
          start: g.start,
          end: g.end,
          resource: g.options[0],
        });
      } else {
        out.push({
          title: `${g.options.length} sessions au choix`,
          start: g.start,
          end: g.end,
          resource: {
            ...g.options[0],
            type: 'public_slot',
            _fused: true,
            _fused_options: g.options.map((opt) => ({
              event_id: opt.event_id,
              title: opt.title,
              duration_minutes: opt.duration_minutes,
            })),
          },
        });
      }
    }
    out.push(...bookedEvents);
    return out;
  }, [events, enabledTypes, windowStart, windowEnd, fuseSlots, bookedRows, liveTick]); // eslint-disable-line react-hooks/exhaustive-deps

  // Brief Jun 11 — système de couleurs unifié (cf. calendarTheme.js + légende)
  const eventStyleGetter = (event) => eventStyleFor(event.resource, 'dark');

  const dayPropGetter = (d) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const candidate = new Date(d);
    candidate.setHours(0, 0, 0, 0);
    if (candidate < today) {
      return { className: 'rbc-day-past' };
    }
    return {};
  };

  return (
    <div className="space-y-3" data-testid="expert-calendar">
      {/* Phase β notice — hidden in compact mode (used by MesSessions wrapper) */}
      {!compact && (
      <div className="flex items-start gap-3 p-4 rounded-xl bg-cyan-500/5 border border-cyan-400/20" data-testid="calendar-phase-alpha-banner">
        <Sparkles className="h-5 w-5 text-cyan-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-cyan-100/90 flex-1">
          <strong className="text-cyan-300">Sprint 1.0b · Phase β (création + fusion slots)</strong>
          <br />
          Vous pouvez désormais créer 4 types d'événements via le bouton « Nouveau ».
          La fusion automatique regroupe les slots empilés sur le même horaire.
          Le calendrier public côté profil expert arrive en phase γ.
        </div>
        <button
          data-testid="calendar-new-event-btn"
          type="button"
          onClick={() => setWizardOpen(true)}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium bg-gradient-to-r from-cyan-500 to-violet-500 text-white hover:opacity-90"
        >
          <Plus className="h-4 w-4" /> Nouveau
        </button>
      </div>
      )}

      {/* Compact-mode header strip — single-line "Nouveau" button + light stats
          Brief Jun 16 §1 : hideNewButton when MySessions provides its own 2 buttons. */}
      {compact && (
        <div className="flex items-center justify-between gap-3 flex-shrink-0 mb-2">
          <div className="text-xs text-cyan-200/80">
            <strong className="text-cyan-300">{stats?.upcoming_count ?? '—'}</strong> à venir · {stats?.total_published ?? 0} publiés
          </div>
          {!hideNewButton && (
            <button
              data-testid="calendar-new-event-btn"
              type="button"
              onClick={() => setWizardOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gradient-to-r from-cyan-500 to-violet-500 text-white hover:opacity-90"
            >
              <Plus className="h-3.5 w-3.5" /> Nouveau
            </button>
          )}
        </div>
      )}

      {/* Stats + Filters — hidden in compact mode (already replaced by header strip) */}
      {!compact && (
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
        <div className="lg:col-span-1 p-4 rounded-xl bg-white/5 border border-white/10" data-testid="calendar-stats">
          <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">Vue d'ensemble</p>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold text-white">{stats?.upcoming_count ?? '—'}</span>
            <span className="text-sm text-gray-400">à venir</span>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {stats?.total_published ?? 0} événements publiés au total
          </p>
        </div>

        <div className="lg:col-span-3 p-4 rounded-xl bg-white/5 border border-white/10">
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <p className="text-xs uppercase tracking-wide text-gray-400">Afficher / masquer</p>
            <label className="inline-flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
              <input
                data-testid="cal-toggle-fuse"
                type="checkbox"
                checked={fuseSlots}
                onChange={(e) => setFuseSlots(e.target.checked)}
                className="accent-cyan-500"
              />
              Fusionner les slots empilés (β)
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            {TYPE_FILTERS.map((f) => {
              const Icon = TYPE_META[f.id].icon;
              const on = enabledTypes[f.id];
              return (
                <button
                  key={f.id}
                  type="button"
                  data-testid={f.testid}
                  onClick={() => setEnabledTypes((prev) => ({ ...prev, [f.id]: !prev[f.id] }))}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-all ${
                    on
                      ? 'bg-cyan-500/15 border-cyan-400/40 text-cyan-200'
                      : 'bg-white/5 border-white/10 text-gray-400 line-through'
                  }`}
                >
                  {on ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                  <Icon className="h-3 w-3" />
                  {f.label}
                  <span className="ml-1 text-[10px] opacity-60">
                    {stats?.by_type?.[f.id] ?? 0}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      )}

      {/* Calendar */}
      <div
        className="rounded-2xl bg-[#0d1f35]/40 border border-white/10 backdrop-blur-md p-3"
        data-testid="calendar-grid"
      >
        {loading ? (
          <div className={compact ? "flex items-center justify-center text-gray-400" : "h-[600px] flex items-center justify-center text-gray-400"} style={compact ? { height: COMPACT_HEIGHT } : undefined}>
            <Loader2 className="h-6 w-6 animate-spin mr-2" /> Chargement du calendrier…
          </div>
        ) : error ? (
          <div className="h-[300px] flex items-center justify-center text-red-300">
            <AlertCircle className="h-5 w-5 mr-2" /> {error}
          </div>
        ) : (
          <div style={compact ? { height: COMPACT_HEIGHT } : { height: 640 }}>
            <BigCalendar
              localizer={localizer}
              events={bigCalEvents}
              date={date}
              onNavigate={setDate}
              view={view}
              onView={setView}
              views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
              defaultView={Views.WEEK}
              culture="fr"
              messages={FR_MESSAGES}
              eventPropGetter={eventStyleGetter}
              dayPropGetter={dayPropGetter}
              components={{ toolbar: ExpertCalendarToolbar, event: CalendarEventContent }}
              tooltipAccessor={(e) => {
                if (e.resource?._kind === 'booked_by_me') return `Session réservée · ${e.title}`;
                return `${TYPE_META[e.resource.type]?.label || 'Événement'} · ${e.title}`;
              }}
              popup
              startAccessor="start"
              endAccessor="end"
              scrollToTime={new Date(new Date().setHours(8, 0, 0, 0))}
              min={new Date(new Date().setHours(0, 0, 0, 0))}
              max={new Date(new Date().setHours(23, 59, 0, 0))}
              step={30}
              timeslots={2}
              onSelectEvent={(slot) => {
                // Brief Jul 27 2026 §5 — les sessions réservées / invitations
                // ouvrent le drawer de détails adapté au rôle (avant : no-op).
                if (slot.resource?._kind === 'booked_by_me') {
                  setDetailsRow(slot.resource);
                  return;
                }
                setDetailsEvent(slot.resource);
              }}
            />
          </div>
        )}
      </div>

      <EventCreationWizard
        isOpen={wizardOpen || !!editingEvent}
        editEvent={editingEvent}
        onClose={() => { setWizardOpen(false); setEditingEvent(null); load(); }}
        onCreated={() => { /* keep wizard open so user sees Step 3 (Jitsi + ICS).
                              Refresh happens when they close via wizard-done-btn → onClose. */ }}
      />

      <EventDetailsModal
        event={detailsEvent}
        isOpen={!!detailsEvent}
        onClose={() => setDetailsEvent(null)}
        onChanged={load}
        onEdit={(ev) => { setDetailsEvent(null); setEditingEvent(ev); }}
        ownerUserId={ownerUserId}
        variant="drawer"
      />

      {/* Brief Jul 27 2026 §5 — drawer par rôle pour réservations/invitations */}
      <SessionDetailsDrawer
        row={detailsRow}
        isOpen={!!detailsRow}
        onClose={() => setDetailsRow(null)}
        currentUserId={ownerUserId}
        onAction={async (kind, id, action) => {
          setDetailsRow(null);
          try {
            if (kind === 'invitation') {
              await axios.post(`${API}/calendar/events/${id}/respond-invitation`, { accept: action === 'accept' }, { withCredentials: true });
              toast.success(action === 'accept' ? 'Invitation acceptée' : 'Invitation déclinée');
            } else if (kind === 'booking') {
              await axios.put(`${API}/bookings/${id}/status?status=${encodeURIComponent(action)}`, null, { withCredentials: true });
            }
            load();
          } catch (e) {
            toast.error(e?.response?.data?.detail || 'Action impossible');
          }
        }}
      />
    </div>
  );
};

export default ExpertCalendar;
